import React from 'react';
import { useTheme } from '../../themes/ThemeProvider';
import { cn } from '../../utils/cn';
import type { FooterColumnConfig } from '../../themes/themeTypes';
import { useLocalizedTo } from '../../hooks/useLocaleNavigation';
import { isInternalPath } from '../../i18n/localeRouting';

const FOOTER_GRID_LAYOUT: Record<string, string> = {
  'one-column': 'grid-cols-1',
  'two-column': 'grid-cols-1 md:grid-cols-2',
  'three-column': 'grid-cols-1 md:grid-cols-3',
  'four-column': 'grid-cols-2 md:grid-cols-4',
};

const MOBILE_COLUMNS_CLASS: Record<number, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-2',
  3: 'grid-cols-3',
};

const FOOTER_PADDING_CLASS: Record<string, string> = {
  compact: 'py-2xl',
  comfortable: 'py-3xl',
  generous: 'py-4xl',
};


const Footer: React.FC = () => {
  const { branding } = useTheme();
  const localizedTo = useLocalizedTo();
  const storeName = branding?.store?.name || branding?.name || 'Store';
  const storeDescription = branding?.store?.description || branding?.description;
  const storeTagline = branding?.store?.tagline || branding?.tagline;
  const footerConfig = branding?.footer;

  const footerLayout = footerConfig?.layout || 'three-column';
  const mobileColumns = footerConfig?.mobileColumns;
  const columns: FooterColumnConfig[] = footerConfig?.columns || [];
  const showNewsletter = footerConfig?.showNewsletter ?? false;
  const newsletterTitle = footerConfig?.newsletterTitle || 'Stay in touch';
  const newsletterSubtitle = footerConfig?.newsletterSubtitle || 'Be the first to know about updates and launches.';
  const socialLinks: Record<string, string> = footerConfig?.social || {};
  const bottomBar = footerConfig?.bottomBar;
  const styling = footerConfig?.styling;

  const textColorKey = styling?.textColor || 'secondary';
  const isLightText = textColorKey === 'light';
  // Map footer.styling.backgroundColor enum to theme CSS vars
  const backgroundColor = (() => {
    const bg = styling?.backgroundColor;
    switch (bg) {
      case 'dark':
        return 'var(--color-primary)';
      case 'surface':
        return 'var(--color-bg-secondary)';
      case 'soft':
        return 'var(--color-bg-soft)';
      case 'transparent':
        return 'transparent';
      default:
        return 'var(--color-bg)';
    }
  })();

  // Derive text colors from footer styling.textColor enum
  const headingColor = isLightText ? 'var(--color-text-on-dark)' : 'var(--color-text-primary)';
  const bodyColor = isLightText ? 'rgba(255, 255, 255, 0.75)' : 'var(--color-text-secondary)';
  const linkColor = isLightText ? 'rgba(255, 255, 255, 0.75)' : 'var(--color-text-secondary)';
  const mutedColor = isLightText ? 'rgba(255, 255, 255, 0.5)' : 'var(--color-text-light)';

  const headingClass = 'text-sm font-semibold uppercase tracking-wide';
  const bodyTextClass = 'text-sm leading-relaxed';
  const linkClass = 'text-sm no-underline transition-colors hover:text-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary';

  const paddingClass = FOOTER_PADDING_CLASS[styling?.padding || 'generous'] || FOOTER_PADDING_CLASS.generous;
  const borderTopClass = styling?.borderTop === false ? '' : 'border-t';
  const borderColor = isLightText ? 'rgba(255, 255, 255, 0.1)' : 'var(--color-border-light)';
  const baseLayoutClass = FOOTER_GRID_LAYOUT[footerLayout] || '';
  const layoutClass = mobileColumns
    ? baseLayoutClass.replace(/grid-cols-\d+/, MOBILE_COLUMNS_CLASS[mobileColumns] || 'grid-cols-1')
    : baseLayoutClass;
  const totalColumnsCount = 1 + columns.length + (showNewsletter ? 1 : 0);
  const responsiveColumnsClass = totalColumnsCount >= 5
    ? 'md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
    : totalColumnsCount === 4
      ? 'md:grid-cols-2 lg:grid-cols-4'
      : totalColumnsCount === 3
        ? 'md:grid-cols-3'
        : 'md:grid-cols-2';

  const resolvedBottomText = bottomBar?.copyright || `© ${new Date().getFullYear()} ${storeName}.`;

  return (
    <footer
      className={cn('mt-16', borderTopClass)}
      style={{ backgroundColor, borderColor }}
    >
      <div className={cn('container mx-auto px-lg', paddingClass)} style={{ maxWidth: 'var(--container-max-width)' }}>
        <div className={cn('grid grid-cols-1 gap-lg md:gap-2xl mb-2xl', layoutClass, responsiveColumnsClass)}>
          {/* Brand Info - First Column */}
          <div className="flex flex-col gap-sm">
            <h3
              className="font-serif text-xl font-semibold"
              style={{ color: headingColor }}
            >
              {storeName}
            </h3>
            {storeDescription ? (
              <p className={bodyTextClass} style={{ color: bodyColor }}>{storeDescription}</p>
            ) : storeTagline ? (
              <p className="text-sm uppercase tracking-[0.3em]" style={{ color: isLightText ? 'rgba(255,255,255,0.7)' : 'var(--color-primary)' }}>
                {storeTagline}
              </p>
            ) : null}

            {Object.entries(socialLinks).length > 0 && (
              <div className="flex flex-wrap gap-md pt-md">
                {Object.entries(socialLinks).map(([network, url]) => (
                  <a
                    key={network}
                    href={url as string}
                    className="text-sm no-underline transition-colors hover:opacity-80"
                    style={{ color: linkColor }}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={`${storeName} on ${network}`}
                  >
                    {network.charAt(0).toUpperCase() + network.slice(1)}
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* Column Links - Before Newsletter */}
          {columns.map((column, index) => (
            <div key={`${column.title}-${index}`} className="flex flex-col gap-sm">
              {column.title && (
                <h4 className={headingClass} style={{ color: headingColor }}>{column.title}</h4>
              )}
              {column.links && column.links.length > 0 && (
                <ul className="space-y-2 list-none p-0 m-0">
                  {column.links?.map((link, linkIndex) => (
                    <li key={`${link.label}-${linkIndex}`}>
                      <a
                        href={isInternalPath(link.path) ? localizedTo(link.path) : link.path}
                        className={linkClass}
                        style={{ color: linkColor }}
                      >
                        {link.label}
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}

          {/* Newsletter - Last Column to prevent overlap */}
          {showNewsletter && (
            <div className="flex flex-col gap-sm">
              <h4 className={headingClass} style={{ color: headingColor }}>{newsletterTitle}</h4>
              <p className={bodyTextClass} style={{ color: bodyColor }}>{newsletterSubtitle}</p>
              <form
                className="flex flex-col gap-sm mt-sm"
                onSubmit={event => event.preventDefault()}
              >
                <input
                  type="email"
                  name="newsletter-email"
                  aria-label="Email address"
                  placeholder="you@example.com"
                  className="w-full rounded-sm border px-md py-sm text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  style={{
                    backgroundColor: isLightText ? 'rgba(255,255,255,0.1)' : 'var(--color-bg)',
                    borderColor: isLightText ? 'rgba(255,255,255,0.2)' : 'var(--color-border)',
                    color: headingColor,
                  }}
                />
                <button
                  type="submit"
                  className="w-full px-lg py-sm bg-primary text-[var(--color-text-on-primary)] rounded-sm text-sm font-semibold transition-all hover:bg-primary-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                >
                  Subscribe
                </button>
              </form>
            </div>
          )}
        </div>

        <div
          className="pt-xl text-center border-t"
          style={{ borderColor }}
        >
          <p className="text-sm" style={{ color: bodyColor }}>
            {resolvedBottomText}
          </p>

          {bottomBar?.links && bottomBar.links.length > 0 && (
            <div className="flex flex-wrap justify-center gap-md mt-sm">
              {bottomBar.links?.map((link, linkIndex) => (
                <a
                  key={`${link.label}-${linkIndex}`}
                  href={isInternalPath(link.path) ? localizedTo(link.path) : link.path}
                  className="text-xs no-underline uppercase tracking-wide transition-colors hover:text-primary"
                  style={{ color: mutedColor }}
                >
                  {link.label}
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
    </footer>
  );
};

export default Footer;
